import {
	Atom,
	AtomOptions,
	ComputedAtom,
	ComputedAtomOptions,
	Reaction,
	ReactionOptions,
	ReactiveLayer,
} from "fluidstate";
import {
	atom,
	computed,
	effect,
	endBatch,
	getCurrentSub,
	reportChanged,
	reportObserved,
	setCurrentSub,
	startBatch,
} from "./alien-signals";

/**
 * Returns a `ReactiveLayer` implementation backed by the Alien Signals library.
 * This function provides the bridge between `fluidstate`'s abstract reactivity
 * API and Alien Signals's reactivity implementation.
 */
export const getReactiveLayer = (): ReactiveLayer => ({
	createAtom: (name: string, options?: AtomOptions): Atom => {
		const alienAtom = atom(options);

		const result = {
			name,
			reportChanged: reportChanged.bind(alienAtom),
			reportObserved: reportObserved.bind(alienAtom),
		};

		return result;
	},

	createComputedAtom: <T>(
		name: string,
		calculate: () => T,
		options?: ComputedAtomOptions
	): ComputedAtom<T> => {
		const get = computed(calculate, options);

		const result = {
			name,
			get,
		};

		return result;
	},

	createReaction: (fn: () => void, options?: ReactionOptions): Reaction => {
		const stopEffect = effect(fn, options);

		const result = {
			stop: stopEffect,
		};

		return result;
	},

	untrack: <T>(action: () => T): T => {
		const prevActiveSub = getCurrentSub();
		setCurrentSub(undefined);
		try {
			return action();
		} finally {
			setCurrentSub(prevActiveSub);
		}
	},

	runTransaction: <T>(transaction: () => T): T => {
		startBatch();
		try {
			return transaction();
		} finally {
			endBatch();
		}
	},

	runAction: <T>(action: () => T): T => {
		startBatch();
		try {
			return action();
		} finally {
			endBatch();
		}
	},
});
